import { useEffect } from 'react';
import { Auth, I18n } from 'aws-amplify';
import { Exit } from 'effect';
import { Authenticator, Grid, useAuthenticator, View } from '@aws-amplify/ui-react';
import components from 'components/Authenticator/components';
import services from 'services';
import { persistBackUrl, redirectToApp, getAppRedirectData, getBackUrl } from 'amplify/services';
import { useLabelOptions } from 'contexts/labelOptionsContext';
import { useAuthContext } from 'contexts/authContext';
import useRedirect from 'hooks/useRedirect';
import AsideCarousel from 'components/AsideCarousel';
import AsideWL from 'components/AsideWL';
import { useAuthorizedUserContext } from 'contexts/authorizedUserContext';

export default function AuthPage() {
  const { route } = useAuthenticator();
  const { labelOptions } = useLabelOptions();
  const { setIsResetPasswordFlow } = useAuthContext();
  const { initialForm } = useRedirect();
  const { setAuthorizedUserContext } = useAuthorizedUserContext();

  const isSignInRoute = route === 'signIn';

  // reset variable isResetPasswordFlow if the user come back to the signIn route
  useEffect(() => {
    if (isSignInRoute) {
      setIsResetPasswordFlow(false);
    }
  }, [isSignInRoute, setIsResetPasswordFlow]);

  useEffect(() => {
    persistBackUrl();
  }, []);

  useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then(async () => {
        const backUrl = getBackUrl();
        // we dont handle cookies for canary delivery and apiEndpoint here as this event only
        // handles page refresh with active  authsession. Cookies are handled on pure login
        // only, when there is no active auth session
        const resultExit = await getAppRedirectData(backUrl);
        Exit.match(resultExit, {
          onFailure: (cause) => {
            console.error('PostLogin API failure:', cause);

            const error = cause?.error;
            const isIpRestriction = error?._tag === 'ValidatedResponseError' &&
              error.status === 403 && error.validatedBody?.type === 'invalid_remote_address';
            const isValidatedResponseError = error?._tag === 'ValidatedResponseError';

            // Set appropriate context based on error type
            setAuthorizedUserContext(
              isIpRestriction ? { isIpRestricted: true } : { redirectError: true }
            );

            // Sign out for: IP restriction OR non-4xx errors
            // Other 4xx errors already signed out in services.ts
            if (isIpRestriction) {
              // Store in sessionStorage to survive the signOut redirect
              sessionStorage.setItem('ipRestricted', 'true');
              Auth.signOut();
            } else if (!isValidatedResponseError) {
              Auth.signOut();
            }
          },
          onSuccess: (data) => {
            // Clear IP restriction flag on successful login
            sessionStorage.removeItem('ipRestricted');

            const { phoneVerificationRequired, redirectUrl } = data
            if (!phoneVerificationRequired) {
              redirectToApp(redirectUrl);
              return;
            }
            // sets phoneVerificationRequired flag that controls extra steps in App.js
            setAuthorizedUserContext({ appUrl: redirectUrl,  phoneVerificationRequired });
          },
        });
      })
      .catch(() => {});
  }, [setAuthorizedUserContext]);

  return (
    <Grid
      templateColumns={{
        xl: isSignInRoute || labelOptions.isWL ? '650px 1fr' : '0 1fr',
        large: isSignInRoute || labelOptions.isWL ? '1fr 2fr' : '0 1fr',
        base: '0 1fr',
      }}
      minHeight="100vh"
      width="100%"
      className="girdLayout"
      style={{
        background: labelOptions.isWL ? '#F7FAFD' : 'linear-gradient(180deg, #0C71E7 0%, #00D487 100%)',
      }}
    >
      <View
        height={{
          base: '0px',
          large: isSignInRoute || labelOptions.isWL ? '100%' : '0px',
        }}
      >
        {labelOptions.isWL ? <AsideWL /> : <AsideCarousel />}
      </View>
      <Authenticator
        hideSignUp
        components={components}
        initialState={initialForm}
        services={services}
        formFields={{
          signIn: {
            username: {
              isRequired: true,
              id: 'username-input',
            },
            acknowledgement: {
              isRequired: true,
              type: 'hiddenField',
            },
          },
          forceNewPassword: {
            password: {
              label: I18n.get('forceNewPassword.newPassword.label'),
              placeholder: I18n.get('forceNewPassword.newPassword.placeholder'),
            },
          },
          confirmResetPassword: {
            password: {
              label: I18n.get('forceNewPassword.newPassword.label'),
              placeholder: I18n.get('forceNewPassword.newPassword.placeholder'),
            },
            confirm_password: {
              placeholder: I18n.get('Please confirm your Password'),
            },
          },
        }}
      />
    </Grid>
  );
}
